import qicardPayments, { normalizeQiCardLanguage } from "../services/qicardPayments";
import { QiCardPendingContext } from "../types/qicard";
import {
  linkTransactionToOrder,
  setActivePaymentSession,
} from "./abandonedOrderPayments";
import { saveQiCardPending } from "./qicardStorage";

export function buildQiCardReturnUrl(): string {
  return `${window.location.origin}/payment/return`;
}

export function persistAndRedirect(
  redirectUrl: string | undefined,
  context: Omit<QiCardPendingContext, "startedAt">
): void {
  if (!context.transactionId?.trim()) {
    throw new Error("missing_transaction");
  }
  if (!redirectUrl?.trim()) {
    throw new Error("missing_redirect");
  }

  saveQiCardPending({
    ...context,
    startedAt: Date.now(),
  });
  linkTransactionToOrder(context.transactionId, context.orderId ?? "");
  if (context.orderId) {
    setActivePaymentSession({
      orderId: context.orderId,
      type: "QiCard",
      transactionId: context.transactionId,
    });
  }
  window.location.assign(redirectUrl);
}

export async function startQiCardOrderPayment(params: {
  orderId: string;
  amount: number;
  language?: string;
}): Promise<void> {
  const returnUrl = buildQiCardReturnUrl();
  const result = await qicardPayments.createOrderPayment({
    orderId: params.orderId,
    amount: params.amount,
    successUrl: returnUrl,
    failedUrl: returnUrl,
    redirectUrl: returnUrl,
    language: normalizeQiCardLanguage(params.language),
  });

  if (result.isDuplicate && result.transactionId) {
    saveQiCardPending({
      transactionId: result.transactionId,
      kind: "order",
      orderId: params.orderId,
      amount: params.amount,
      startedAt: Date.now(),
    });
    window.location.assign(returnUrl);
    return;
  }

  persistAndRedirect(result.redirectUrl, {
    transactionId: result.transactionId,
    kind: "order",
    orderId: params.orderId,
    amount: params.amount,
  });
}

export async function startQiCardWalletRecharge(params: {
  amount: number;
  language?: string;
}): Promise<void> {
  const returnUrl = buildQiCardReturnUrl();
  const result = await qicardPayments.createWalletRecharge({
    amount: params.amount,
    successUrl: returnUrl,
    failedUrl: returnUrl,
    redirectUrl: returnUrl,
    language: normalizeQiCardLanguage(params.language),
  });

  persistAndRedirect(result.redirectUrl, {
    transactionId: result.transactionId,
    kind: "wallet",
    amount: params.amount,
  });
}

export type QiCardPaymentErrorCode =
  | "missing_redirect"
  | "missing_transaction"
  | "generic";

export function mapQiCardPaymentError(err: unknown): QiCardPaymentErrorCode {
  if (err instanceof Error) {
    if (err.message === "missing_redirect") return "missing_redirect";
    if (err.message === "missing_transaction") return "missing_transaction";
  }
  return "generic";
}

export function isQiCardAlreadyCompletedCancelError(err: unknown): boolean {
  const axiosErr = err as {
    response?: { status?: number; data?: { error?: string } };
  };
  if (axiosErr?.response?.status !== 400) return false;
  const message = String(axiosErr.response.data?.error ?? "").toLowerCase();
  return message.includes("already completed");
}
